/**
 * Retrieval: find the objects a question is about, not the ones that spell it.
 *
 * `ACCEPTANCE.md`'s Fabric row asks for *"semantic retrieval"*, and the word
 * has to be earned rather than borrowed. This is what it means here, stated
 * before anything else so nobody has to infer it from the code:
 *
 * > **Semantic, from this pool's own language — not from a pretrained model.**
 *
 * There is no embedding model and no service call. Two things rule that out and
 * both are already settled: decision 0004's stack, and the Obsidian row's
 * *"local-first with no server"*. A retrieval that phoned an embedding API to
 * answer a search would break the second every time somebody typed.
 *
 * So the meaning comes from the corpus. Two mechanisms, both computed from the
 * objects in scope:
 *
 *  1. **BM25** ranks by term rarity and document length rather than by counting
 *     substrings. A word that appears in every object tells you nothing about
 *     which one you want; a word that appears in two tells you a great deal.
 *     `includes()` cannot distinguish them.
 *  2. **Co-occurrence expansion** is the part that earns the word. Terms that
 *     keep appearing near each other in *this pool* are treated as related, so
 *     a search for one retrieves objects that are about it without containing
 *     it. That relatedness is learned from the pool's own text, which is why it
 *     is right for a vault of somebody's notes and would be wrong as a
 *     shipped-in dictionary.
 *
 * **What it is not.** It has no idea that "car" and "automobile" are synonyms
 * unless this pool uses them together. A pretrained model would; that is the
 * trade for not having a server, and it is a real limit rather than a detail.
 */
import type { ObjectId } from './ids.js';

/** A word, for indexing purposes.
 *
 *  Unicode-aware on purpose: `[a-z0-9]+` drops every accented character, so a
 *  French film pool would index "Plein" and "Soleil" but lose "Clément"
 *  entirely — and the failure is invisible, because the objects still appear
 *  under their other words. */
const WORD = /[\p{L}\p{N}][\p{L}\p{N}'-]*/gu;

/**
 * Words too common in ANY corpus to carry meaning.
 *
 * Deliberately short. A long stop list is a dictionary by another name, and
 * this file's whole argument is that meaning comes from the pool rather than
 * from something shipped in. BM25 already discounts a term that appears
 * everywhere; this only removes the handful whose document frequency is high in
 * every corpus, so a two-object pool does not have to learn it the hard way.
 */
const STOP = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'but', 'by', 'for', 'from',
  'has', 'have', 'in', 'is', 'it', 'its', 'of', 'on', 'or', 'that', 'the',
  'this', 'to', 'was', 'were', 'will', 'with',
]);

export function terms(text: string): string[] {
  const out: string[] = [];
  for (const m of text.toLowerCase().matchAll(WORD)) {
    const w = m[0];
    // One-character words carry no signal and blow up the co-occurrence matrix.
    if (w.length > 1 && !STOP.has(w)) out.push(w);
  }
  return out;
}

/** One indexed object: its id and the text that describes it. */
export interface Indexable {
  readonly id: ObjectId;
  readonly text: string;
}

export interface Hit {
  readonly id: ObjectId;
  readonly score: number;
  /** Terms that matched, including ones the query never contained — those are
   *  what the expansion contributed, and a caller that wants to explain a
   *  result needs to be able to say so. */
  readonly matched: readonly string[];
}

/** BM25's two constants, at the values the literature settles on. Named rather
 *  than inlined so a reader can see there are exactly two and neither is
 *  tuned to this repo's fixtures. */
const K1 = 1.5;
const B = 0.75;

/** How many related terms a query word may pull in. Small on purpose: an
 *  expansion that adds twenty terms retrieves the whole pool and calls it a
 *  match, which is worse than retrieving nothing. */
const EXPAND_PER_TERM = 2;
/** How strongly an expanded term counts against a term the person typed. A
 *  document that actually contains the word must outrank one that merely
 *  contains its neighbours. */
const EXPANSION_WEIGHT = 0.35;

/**
 * An index over the text of a set of objects.
 *
 * Built from what it is given — the caller decides which roles are worth
 * indexing, because the store must not assume prose is the only meaningful
 * text. Titles matter, and so do captions.
 */
export class TextIndex {
  private readonly docs: { id: ObjectId; len: number; tf: Map<string, number> }[] = [];
  private readonly df = new Map<string, number>();
  /** term -> term -> how often they appeared in the same object. The matrix
   *  this pool's own language is learned from. */
  private readonly near = new Map<string, Map<string, number>>();
  private avgLen = 0;

  constructor(items: readonly Indexable[]) {
    for (const it of items) {
      const ws = terms(it.text);
      const tf = new Map<string, number>();
      for (const w of ws) tf.set(w, (tf.get(w) ?? 0) + 1);
      this.docs.push({ id: it.id, len: ws.length, tf });
      for (const w of tf.keys()) this.df.set(w, (this.df.get(w) ?? 0) + 1);

      // Co-occurrence, counted once per PAIR per document rather than once per
      // occurrence. Counting occurrences makes a word repeated forty times in
      // one long note look like a strong relationship with everything else in
      // that note, which is a property of the note's length and not of the
      // language.
      const unique = [...tf.keys()];
      for (const a of unique) {
        let row = this.near.get(a);
        if (!row) { row = new Map(); this.near.set(a, row); }
        for (const b of unique) {
          if (a !== b) row.set(b, (row.get(b) ?? 0) + 1);
        }
      }
    }
    this.avgLen = this.docs.length
      ? this.docs.reduce((n, d) => n + d.len, 0) / this.docs.length : 0;
  }

  get size(): number { return this.docs.length; }

  /** How many shared documents a pair needs before it counts as related. One
   *  below twenty objects, two above — see the filter in `related`. */
  private coocFloor(): number {
    return this.docs.length < 20 ? 1 : 2;
  }

  /** Inverse document frequency, the BM25 form. The `+ 1` inside the log keeps
   *  it positive for a term in every document — the unclamped form goes
   *  negative there and a common word would then push a result DOWN the list,
   *  which reads as the search actively avoiding what was asked for. */
  private idf(term: string): number {
    const n = this.docs.length;
    const d = this.df.get(term) ?? 0;
    return Math.log(1 + (n - d + 0.5) / (d + 0.5));
  }

  /**
   * Terms this pool treats as related to `term`.
   *
   * Ranked by how much more often they co-occur than their own frequency would
   * predict, so a word that appears everywhere is not "related" to everything.
   */
  related(term: string): string[] {
    const row = this.near.get(term);
    if (!row) return [];
    const own = this.df.get(term) ?? 0;
    // A term that appears in ONE document is that document's vocabulary, not
    // the pool's language, and it has no evidence of being about anything.
    // Without this the measure below is maximal for exactly those words: a
    // hapax that happens to sit beside the query term shares 100% of its
    // occurrences with it. Measured on a five-note fixture, "pagination"
    // expanded to "breaks" and "document" — both appearing once, both in the
    // same note — while "printing", the word the pool actually associates with
    // it, was ranked below them and cut.
    if (own < 2) return [];
    return [...row.entries()]
      .filter(([other]) => (this.df.get(other) ?? 0) >= 2)
      .map(([other, together]) => {
        const theirs = this.df.get(other) ?? 1;
        // JACCARD: shared documents over the documents either appears in. It
        // asks how much of their combined footprint they share, which no
        // amount of rarity can inflate — unlike dividing by the smaller
        // frequency, where one occurrence is always a perfect score.
        return { other, lift: together / (own + theirs - together) };
      })
      .filter((x) => x.lift >= 0.25
        && (this.near.get(term)?.get(x.other) ?? 0) >= this.coocFloor())
      // Ties broken by term so the expansion is deterministic — an expansion
      // that varies between identical searches makes the result list vary too.
      .sort((a, b) => b.lift - a.lift || (a.other < b.other ? -1 : 1))
      .slice(0, EXPAND_PER_TERM)
      .map((x) => x.other);
  }

  /**
   * The objects this query is about, best first.
   *
   * Returns an empty list for an empty query rather than everything: "no
   * search" and "a search that matched the whole pool" are different answers,
   * and a caller that cannot tell them apart will show the second when it means
   * the first.
   */
  search(query: string, limit = 20): Hit[] {
    const asked = [...new Set(terms(query))];
    if (!asked.length || !this.docs.length) return [];

    // Each query term carries full weight; each expanded term carries less, and
    // never overrides a term the person actually typed.
    const weights = new Map<string, number>();
    for (const t of asked) weights.set(t, 1);
    for (const t of asked) {
      for (const r of this.related(t)) {
        if (!weights.has(r)) weights.set(r, EXPANSION_WEIGHT);
      }
    }

    const hits: Hit[] = [];
    for (const doc of this.docs) {
      let score = 0;
      const matched: string[] = [];
      for (const [term, weight] of weights) {
        const f = doc.tf.get(term);
        if (!f) continue;
        const norm = f * (K1 + 1)
          / (f + K1 * (1 - B + B * (doc.len / (this.avgLen || 1))));
        score += weight * this.idf(term) * norm;
        matched.push(term);
      }
      if (score > 0) hits.push({ id: doc.id, score, matched });
    }
    return hits
      // Ties broken by id so the same query twice gives the same order — a
      // result list that reshuffles between identical searches reads as the
      // page having changed its mind.
      .sort((a, b) => b.score - a.score || (a.id < b.id ? -1 : 1))
      .slice(0, limit);
  }
}
